import {
    Button,
    CircularProgress,
    FormControl,
    FormHelperText,
    FormLabel,
    OutlinedInput,
    Select,
    MenuItem,
    Typography,
    Box,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { exportFileWithInputs } from 'store';
import classes from "./ExportFile.module.scss";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { campaignNavigatorUrl } from "constants";
import { exportClusterFile } from "store";

const ExportFileBox = React.memo(({ onCancel }) => {

    const dispatch = useDispatch();
    const [fileName, setFileName] = useState("");
    const [storageType, setStorageType] = useState("AWS");
    const [sasUri, setSasUri] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const [columns, setColumns] = useState(["Cluster"]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [outputFile, setOutputFile] = useState("");

    const { inputDataFileName, loadingImportFiles } = useSelector(
        (state) => state.segmenter
    );

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const {
        exportFileApiResponseMsg,
        clusterData,
        loadingExportFileApiResponse,
    } = useSelector((state) => state.segmenter);

    const handleStorageTypeChange = (event) => {
        setStorageType(event.target.value);
    };

    const handleUriChange = (event) => {
        setSasUri(event.target.value);
    };

    const handleSubmision = () => {
        if (storageType === 'COD') {
            exportCsv();
            setSnackbarOpen(true);
        }
        else if (storageType === 'CU360') {
            handleClusterFileExport();
        }
        else {
            dispatch(exportFileWithInputs({ filename: fileName, storageType, sasUri, clusterData }))
            setSnackbarOpen(true);
        }
       
    };

    const handleInputChange = (event) => {
        setFileName(event.target.value);
    };

    const getColumns = async (file) => {

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(
                `${campaignNavigatorUrl}api/v1/amai/get_cluster_columns`
                , {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ file })
                }
            );
            if (!res.ok) {
                throw new Error(`HTTP error! status: ${res.status}`);
            }

            const json = await res.json();
            setColumns(json.columns);
            setOutputFile(res.outputFile);

        } catch (err) {
            console.error("Failed to fetch columns:", err);
            setError(err.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!loadingImportFiles && inputDataFileName) {
            getColumns(inputDataFileName);
        }
    }, [loadingImportFiles, inputDataFileName]);

    // Handle checkbox selection
    const handleSelect = (col, checked) => {
        if (checked) {
            setSelectedColumns(prev => [...prev, col]);
        } else {
            setSelectedColumns(prev => prev.filter(c => c !== col));
        }
    };

    const selectAll = () => setSelectedColumns([...columns]);
    const unselectAll = () => setSelectedColumns([]);

    // Export CSV
    const exportCsv = async () => {
        if (selectedColumns.length === 0) return;

        try {
            setLoading(true);

            const res = await fetch(`${campaignNavigatorUrl}api/v1/amai/export_cluster_csv`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ file: inputDataFileName, columns: selectedColumns, outputFile: outputFile })
            });

            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = "export.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();

        } catch (err) {
            console.error("Export CSV failed:", err);
            setError(err.message || "Export failed");
        } finally {
            setLoading(false);
        }
    };

    // Filtered columns based on search
    const filteredColumns = columns.filter(col =>
        col.toLowerCase().includes(search.toLowerCase())
    );

    const handleClusterFileExport = () => {

        const payload = {
            input_file: inputDataFileName,
            output_name: `${fileName || 'data'}.csv`,
        }

        dispatch(exportClusterFile(payload))
            .unwrap()
            .then((data) => {

                const url = data?.url;
                const link = document.createElement('a');
                link.href = url;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setSnackbarOpen(true);

            })
            .catch((err) => {
                throw err;
            });
    }

    return (
        <Box className={classes.exportFileInnerDailog}>

            <Box className={classes.body}>
                <section className={classes.section}>
                    <section className={classes.header}>
                        <Typography className={classes.title}>Export Data</Typography>
                        <Typography className={classes.label}>Select a file below to begin importing data</Typography>
                    </section>
                    <section className={classes.formSection}>
                        <FormControl
                            component="fieldset"
                            className={classes.formControlStyles}
                        >
                            <FormLabel id="destination-options-label">
                                Select Destination:
                            </FormLabel>
                            <Select
                                labelId="destination-options-label"
                                value={storageType}
                                onChange={handleStorageTypeChange}
                                displayEmpty
                                input={<OutlinedInput />}
                                className={classes.inputBoxStyle}
                            >
                                <MenuItem value="AWS">AWS</MenuItem>
                                <MenuItem value="AEP">AEP Data Landing Zone</MenuItem>
                                <MenuItem value="CU360">Customer360</MenuItem>
                                {/* <MenuItem value="COD">Cluster Output Data</MenuItem> */}
                            </Select>
                        </FormControl>
                        {storageType === "AEP" && (
                            <>
                                <FormControl
                                    component="fieldset"
                                    className={classes.formControlStyles}
                                >
                                    <FormLabel id="uri-input-label">Enter SAS URI:</FormLabel>
                                    <OutlinedInput
                                        placeholder="SAS URI"
                                        value={sasUri}
                                        onChange={handleUriChange}
                                        className={classes.inputBoxStyle}
                                    />
                                </FormControl>
                                <FormControl
                                    component="fieldset"
                                    className={classes.formControlStyles}
                                >
                                    <FormLabel id="radio-buttons-group-label">
                                        Please enter the file name below:
                                    </FormLabel>
                                    {
                                        <OutlinedInput
                                            placeholder="File name"
                                            onChange={handleInputChange}
                                            className={classes.inputBoxStyle}
                                            value={fileName}
                                        />
                                    }
                                    <FormHelperText className={classes.secondaryInfo}>
                                        Your data will be saved as CSV file.
                                    </FormHelperText>
                                </FormControl></>
                        )}

                        {storageType === "CU360" && (
                            <FormControl
                                component="fieldset"
                                className={classes.formControlStyles}
                            >
                                <FormLabel id="radio-buttons-group-label">
                                    Please enter the file name below:
                                </FormLabel>
                                {
                                    <OutlinedInput
                                        placeholder="File name"
                                        onChange={handleInputChange}
                                        className={classes.inputBoxStyle}
                                        value={fileName}
                                    />
                                }
                                <FormHelperText className={classes.secondaryInfo}>
                                    Your data will be saved as CSV file.
                                </FormHelperText>
                            </FormControl>
                        )}

                        {storageType === "COD" && (
                            <FormControl
                                component="fieldset"
                                className={classes.formControlStyles}
                            >
                                <FormLabel id="radio-buttons-group-label">
                                    Please select the columns for the Output File:
                                </FormLabel>
                                {loading && <p>Loading...</p>}
                                {error && <p style={{ color: "red" }}>{error}</p>}

                                {!loading && !error && (
                                    <>
                                        <Box className={classes.inputButtons} >
                                            <Button onClick={selectAll} variant="contained" className={classes.inputButton}>Select All</Button>
                                            <Button onClick={unselectAll} variant="contained" className={classes.inputButton}>Unselect All</Button>
                                            <OutlinedInput
                                                className={classes.inputSearchStyle}
                                                placeholder="Search columns..."
                                                value={search}
                                                onChange={e => setSearch(e.target.value)}
                                                style={{ flex: 1, padding: "4px 8px", borderRadius: "6px", border: "1px solid #ccc" }}
                                            />
                                        </Box>

                                        <Box className={classes.fileColumns}>
                                            {filteredColumns.map(col => (
                                                <FormControlLabel
                                                    className={classes.fileLabel}
                                                    key={col}
                                                    control={
                                                        <Checkbox
                                                            checked={selectedColumns.includes(col)}
                                                            onChange={(e) => handleSelect(col, e.target.checked)}
                                                        />
                                                    }
                                                    label={col}
                                                />
                                            ))}
                                        </Box>
                                    </>
                                )}

                                <FormHelperText className={classes.secondaryInfo}>
                                    Your data will be saved as CSV file.
                                </FormHelperText>
                            </FormControl>
                        )}


                    </section>
                </section>
                <section className={classes.footer}>
                    <Button onClick={onCancel} variant="outlined" className={classes.boxButton} disabled={loadingExportFileApiResponse}>
                        Cancel
                    </Button>

                    {storageType !== "COD" && (
                        <Button onClick={handleSubmision} variant="contained" className={classes.boxButton}
                            startIcon={loadingExportFileApiResponse ? <CircularProgress size={20} /> : null}>
                            {loadingExportFileApiResponse ? 'Exporting...' : 'Export'}
                        </Button>
                    )}

                    {storageType === "COD" && (
                        <Button onClick={handleSubmision} variant="contained" className={classes.boxButton}
                            startIcon={loadingExportFileApiResponse ? <CircularProgress size={20} /> : null}>
                            {loadingExportFileApiResponse ? 'Exporting...' : 'Export'}
                        </Button>
                    )}

                </section>

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={5000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <MuiAlert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                        {exportFileApiResponseMsg}
                    </MuiAlert>
                </Snackbar>
            </Box>
        </Box>
    );
});

export default ExportFileBox;
